import React, { FunctionComponent, ReactElement, useContext, useState } from 'react';
import styled from '@emotion/styled';
import { Link, Modal, Button } from '@ds/ui';
import { inkSpacingTokens } from '@ds/tokens';
import { Interpolate, TranslationContext } from '@ds/react-utils';
import { ConditionalChildren } from 'layouts';

type ResendModalProps = {
    countdown: number;
    resetCountdown: () => void;
    method: string;
    displayChannel: string;
    onNewSms: () => void;
    onNewCall: () => void;
};

const ResendModalContainer = styled.div`
    margin-top: ${inkSpacingTokens.spacingXS};
`;

const ResendModal: FunctionComponent<ResendModalProps> = ({
    countdown,
    resetCountdown,
    method,
    displayChannel,
    onNewSms,
    onNewCall
}): ReactElement | null => {
    const translate = useContext(TranslationContext);
    const [modalVisible, setModalVisible] = useState(false);

    const onSMSClick = () => {
        setModalVisible(false);
        resetCountdown();
        onNewSms();
    };

    const onCallClick = () => {
        setModalVisible(false);
        resetCountdown();
        onNewCall();
    };

    return (
        <ResendModalContainer>
            <Link discrete={true} text={translate('Resend Code')} data-qa={'verify-resend-modal-link'} onClick={() => setModalVisible(true)} />
            <Modal
                width="small"
                onClose={() => setModalVisible(false)}
                visible={modalVisible}
                closeButton={
                    <div data-qa="modal-close">
                        <Modal.Close onClick={() => setModalVisible(false)} />
                    </div>
                }
            >
                <Modal.Header title={translate('Resend code')} data-qa={'verify-resend-modal-header'} />

                <Modal.Body>
                    <div data-qa={'verify-resend-modal-content'}>
                        <ConditionalChildren condition={countdown > 0} fallback={<>{translate('Get your new code now.')}</>}>
                            <Interpolate
                                text={translate('Please wait a moment: <Countdown></Countdown>')}
                                components={{
                                    Countdown: () => countdown.toString()
                                }}
                            />
                        </ConditionalChildren>
                    </div>
                </Modal.Body>

                <ConditionalChildren condition={method === 'SMS'}>
                    <Modal.Footer
                        primaryButton={
                            <Button
                                size="small"
                                kind="primary"
                                data-qa={'verify-resend-modal-primary-button'}
                                text={translate('Resend Code')}
                                onClick={onSMSClick}
                                disabled={countdown !== 0}
                            />
                        }
                        secondaryButton={
                            displayChannel === 'SMSandPhoneCall' ? <Button
                                size="small"
                                kind="tertiary"
                                data-qa={'verify-resend-modal-secondary-button'}
                                text={translate('Get a Call Instead')}
                                onClick={onCallClick}
                                disabled={countdown !== 0}
                            /> : undefined
                        }
                    />
                </ConditionalChildren>
                <ConditionalChildren condition={method === 'PhoneCall'}>
                    <Modal.Footer
                        primaryButton={
                            <Button
                                size="small"
                                kind="primary"
                                data-qa={'verify-resend-modal-primary-button'}
                                text={translate('Get a Call')}
                                onClick={onCallClick}
                                disabled={countdown !== 0}
                            />
                        }
                        secondaryButton={
                             displayChannel === 'SMSandPhoneCall' ? <Button
                                size="small"
                                kind="tertiary"
                                data-qa={'verify-resend-modal-secondary-button'}
                                text={translate('Get a SMS Instead')}
                                onClick={onSMSClick}
                                disabled={countdown !== 0}
                            /> : undefined
                        }
                    />
                </ConditionalChildren>
            </Modal>
        </ResendModalContainer>
    );
};

export default ResendModal;
